import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { MacroToolsStrip } from "@/components/site/MacroToolsStrip";
import { CentralBankGoldTracker } from "@/components/tools/CentralBankGoldTracker";
import { getCentralBankGold } from "@/lib/central-bank-gold.functions";
import { IMF_SOURCE_URL, fmtPeriod, type CbgDTO } from "@/lib/central-bank-gold";

const SITE = "https://xauproacademy.com";
const PATH = "/gold-central-bank-reserves";
const TITLE = "Central Bank Gold Reserves by Country: Monthly Tracker | XAU Pro Academy";
const DESC =
  "Official central bank gold reserves by country in tonnes, updated monthly from IMF data. See which central banks are buying or selling gold, 12 month changes and gold's share of reserves.";

const FAQ = [
  {
    q: "Which country has the largest gold reserves?",
    a: "The United States, with about 8,133 tonnes held by the US Treasury. Germany, Italy, France, China and Russia follow. The table above ranks every central bank that reports gold holdings to the IMF.",
  },
  {
    q: "Which central banks are buying gold right now?",
    a: "The buyers panel lists the largest net additions over the last 12 months from official monthly filings. In recent years the most consistent buyers have been emerging market central banks such as Poland, China, Kazakhstan and the Czech Republic.",
  },
  {
    q: "Why do central banks buy gold?",
    a: "Gold has no issuer and no counterparty, cannot be frozen through a foreign payment system, and holds value across currency regimes. Central banks use it to diversify reserves away from any single currency and to hold an asset that keeps purchasing power over long periods.",
  },
  {
    q: "How do central bank purchases affect the gold price?",
    a: "Official buying is large, steady and largely insensitive to price, so it adds a structural layer of demand under the market. It explains why pullbacks can be absorbed, but it does not time entries. Monthly data is published weeks after the fact.",
  },
  {
    q: "Where does this data come from?",
    a: "From the IMF International Reserves and Foreign Currency Liquidity dataset, which national authorities file every month. Gold is reported in millions of fine troy ounces and converted here at 32,150.75 ounces per tonne.",
  },
  {
    q: "Why do these figures differ from the World Gold Council?",
    a: "The World Gold Council compiles holdings from several sources, includes some countries that do not file with this IMF template, and publishes on its own schedule. Small differences in timing and coverage are normal. This page shows only figures reported by the authorities themselves.",
  },
  {
    q: "Why is Taiwan or Uzbekistan missing?",
    a: "Only authorities that report through the IMF reserves template appear. Taiwan, Uzbekistan, Iraq, Qatar and several other holders do not file in this dataset, so they are not listed rather than estimated.",
  },
  {
    q: "What does gold share of reserves mean?",
    a: "The market value of a country's gold divided by its total official reserve assets. A high share, as in Germany or Italy, means gold dominates reserves. A low share, as in China or Japan, means gold is a small part of much larger currency holdings.",
  },
  {
    q: "Why is the United States marked as book value?",
    a: "The US Treasury values its gold at a statutory price of $42.22 per ounce. Saudi Arabia, Singapore and South Korea also report at book or historical cost. Their tonnes are accurate, so the gold share is restated at the month end market price.",
  },
  {
    q: "Can central bank buying be used as a trading signal?",
    a: "No. It is slow moving context for long term bias. Data arrives with a lag of one to two months and carries no entry, stop or invalidation. Execution still depends on structure, liquidity and displacement on intraday timeframes.",
  },
];

const RELATED = [
  { to: "/gold-real-yield", label: "Macro driver", title: "US 10Y real yield and gold", body: "Why gold moves against real yields, and when it stops doing so." },
  { to: "/gold-dxy-correlation", label: "Currency", title: "Gold and DXY correlation", body: "How the dollar and gold interact on daily data." },
  { to: "/gold-cot-report", label: "Positioning", title: "Gold COT report", body: "What hedge funds and commercials did in COMEX gold this week." },
  { to: "/macrolens", label: "Confluence", title: "XAU MacroLens™", body: "Macro and higher timeframe structure combined into one reading." },
] as const;

export const Route = createFileRoute("/gold-central-bank-reserves")({
  loader: () => getCentralBankGold(),
  staleTime: 60 * 60 * 1000,
  head: ({ loaderData }) => {
    const period = loaderData ? fmtPeriod(loaderData.latestPeriod) : "";
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESC },
        {
          name: "keywords",
          content:
            "central bank gold reserves, gold reserves by country, central bank gold buying, central bank gold purchases, which central banks are buying gold, country gold holdings tonnes, china gold reserves, poland gold reserves, russia gold reserves, us gold reserves, official gold holdings, gold share of reserves, imf gold reserves data, de-dollarization gold",
        },
        { property: "og:title", content: "Central Bank Gold Reserves Tracker: Who Is Buying Gold" },
        {
          property: "og:description",
          content: `Official gold holdings for every reporting central bank, with 1 month, year to date and 12 month changes${period ? ` through ${period}` : ""}.`,
        },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `${SITE}${PATH}` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Central Bank Gold Reserves Tracker: Who Is Buying Gold" },
        { name: "twitter:description", content: "Official monthly gold holdings by country, buyers, sellers and gold's share of reserves." },
      ],
      links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Dataset",
            name: "Central Bank Gold Reserves by Country",
            description:
              "Monthly official gold holdings in tonnes for central banks reporting to the IMF International Reserves and Foreign Currency Liquidity template, with 1 month, year to date and 12 month changes and gold's share of official reserves.",
            url: `${SITE}${PATH}`,
            license: `${SITE}/terms`,
            isAccessibleForFree: true,
            inLanguage: "en",
            variableMeasured: ["Official gold holdings (tonnes)", "Monthly change", "12 month change", "Gold share of official reserves"],
            temporalCoverage: loaderData?.countries[0]?.series[0] ? `${loaderData.countries[0].series[0][0]}/${loaderData.latestPeriod}` : undefined,
            spatialCoverage: "Worldwide",
            dateModified: loaderData?.generatedAt.slice(0, 10),
            isBasedOn: IMF_SOURCE_URL,
            creator: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Central Bank Gold Reserves", item: `${SITE}${PATH}` },
            ],
          }),
        },
      ],
    };
  },
  component: CentralBankReservesPage,
});

const t1 = (x: number) => x.toLocaleString("en-US", { maximumFractionDigits: 1 });
const signedT = (x: number | null) => (x == null ? "n/a" : `${x > 0 ? "+" : ""}${t1(x)} t`);

function Summary({ data }: { data: CbgDTO }) {
  const fresh = data.countries.filter((c) => !c.stale);
  const top = [...fresh].sort((a, b) => (b.m12 ?? 0) - (a.m12 ?? 0)).slice(0, 3).filter((c) => (c.m12 ?? 0) > 0.5);
  const seller = [...fresh].sort((a, b) => (a.m12 ?? 0) - (b.m12 ?? 0))[0];
  const china = data.countries.find((c) => c.code === "CHN");
  return (
    <div className="space-y-4 text-muted-foreground">
      <p>
        Through {fmtPeriod(data.latestPeriod)}, the {data.totals.reporting} institutions filing with the IMF reported{" "}
        <span className="text-foreground">{t1(data.totals.tonnes)} tonnes</span> of official gold. Over the last 12 months their combined
        holdings changed by <span className="text-foreground">{signedT(data.totals.m12Net)}</span>, with {data.totals.buyers12} net buyers
        and {data.totals.sellers12} net sellers.
      </p>
      {top.length > 0 && (
        <p>
          The largest additions came from {top.map((c, i) => (
            <span key={c.code}>
              {i > 0 ? (i === top.length - 1 ? " and " : ", ") : ""}
              <span className="text-foreground">{c.name}</span> ({signedT(c.m12)})
            </span>
          ))}
          .{" "}
          {seller && (seller.m12 ?? 0) < -0.5 && (
            <>
              The largest reduction was {seller.name} ({signedT(seller.m12)}).
            </>
          )}
        </p>
      )}
      {china && (
        <p>
          China reported {t1(china.tonnes)} tonnes as of {fmtPeriod(china.asOf)}, a change of {signedT(china.m12)} over 12 months. Gold
          still represents only {china.goldShare == null ? "a small part" : `${china.goldShare.toFixed(1)}%`} of its official reserves.
        </p>
      )}
    </div>
  );
}

function CentralBankReservesPage() {
  const data = Route.useLoaderData();
  return (
    <Layout>
      <section className="container mx-auto max-w-7xl px-4 pt-14 md:pt-20">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Official monthly data</div>
        <h1 className="mt-3 max-w-3xl font-display text-3xl font-semibold leading-tight md:text-5xl">
          Central Bank Gold Reserves Tracker
        </h1>
        <p className="mt-4 max-w-2xl text-muted-foreground md:text-lg">
          How much gold every reporting central bank holds, who added or reduced reserves this year, and how large gold is within each
          country's official reserves.
        </p>
      </section>

      <section className="container mx-auto mt-10 max-w-6xl px-4">
        <CentralBankGoldTracker data={data} />
        <p className="mt-3 text-[11px] text-muted-foreground">
          Source:{" "}
          <a href={IMF_SOURCE_URL} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">
            IMF International Reserves and Foreign Currency Liquidity
          </a>
          , as filed by national authorities. Tonnes are converted from fine troy ounces. Dates highlighted in gold mark reporters more
          than six months behind, excluded from the 12 month totals. Long term context, not a trade signal.
        </p>
      </section>

      <section className="container mx-auto mt-16 max-w-4xl px-4">
        <h2 className="font-display text-2xl font-semibold md:text-3xl">Central bank gold buying: the latest reading</h2>
        <div className="mt-4">
          <Summary data={data} />
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">Why central banks buy gold</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p>
            Most reserves sit in foreign currency bonds and deposits. Each of those is a claim on another government or bank, and it can
            be frozen through the payment system that holds it. Gold held at home is nobody's liability. That property became central to
            reserve policy after Russian currency reserves were immobilised in 2022.
          </p>
          <p>
            According to the World Gold Council, central banks bought more than 1,000 tonnes of gold in each of 2022, 2023 and 2024,
            roughly double the average of the previous decade. Buying has been led by emerging market central banks whose gold share of
            reserves is still low compared with Europe and the United States.
          </p>
          <p>
            Other motives are diversification away from any single reserve currency, long run protection of purchasing power, and
            confidence. A reserve stock that holds value in a currency crisis supports the domestic currency itself.
          </p>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">What official buying means for XAUUSD</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p>
            Central banks buy on multi year allocation targets, not on price levels. That makes official demand one of the few flows in
            gold that does not chase momentum or flee on drawdowns. It is a structural bid under the market, which helps explain why
            gold has at times rallied while real yields and the dollar were also rising. Compare the{" "}
            <Link to="/gold-real-yield" className="text-gold hover:underline">real yield tracker</Link> and the{" "}
            <Link to="/gold-dxy-correlation" className="text-gold hover:underline">gold and DXY correlation</Link> to see when the usual
            macro relationships weakened.
          </p>
          <p>
            For a trader the reading is context, not timing. Data lands weeks after the month closes, and a strong year of buying says
            nothing about where the next liquidity sweep or displacement leg forms. Use it to frame long term bias, then execute from
            structure on the chart.
          </p>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">How to read the table</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p>
            <span className="text-foreground">Tonnes</span> is the latest reported volume. <span className="text-foreground">1M</span>,{" "}
            <span className="text-foreground">YTD</span> and <span className="text-foreground">12M</span> compare it with the previous
            month, the previous December and the same month a year earlier. A change of zero usually means no transactions, though small
            changes can also come from coin programmes or gold swaps.
          </p>
          <p>
            <span className="text-foreground">Gold share</span> is the market value of gold divided by total official reserve assets.
            Because the gold price has risen, many countries now show a higher gold share without buying a single ounce.
          </p>
          <p>
            <span className="text-foreground">Book value</span> marks reporters that carry gold at a statutory or historical price, such as
            the US Treasury at $42.22 per ounce. Their volume is correct, and their gold share is restated at market.
          </p>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">Data notes and limitations</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p>
            Figures are read from the IMF reserves template every few hours and refreshed when national authorities file a new month.
            If the IMF service is unavailable the page keeps showing the last good dataset. Reporting lags differ by country, so always
            read the As of column.
          </p>
          <p>
            Holders that do not file this template, including Taiwan, Uzbekistan, Iraq and Qatar, are not listed rather than estimated.
            Values filed in an inconsistent unit are corrected only when the reported dollar value confirms the scale, and filings that
            cannot be reconciled are left out. The euro area total is excluded because it would double count its national central banks.
          </p>
        </div>
      </section>

      <section className="container mx-auto mt-16 max-w-7xl px-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Related macro tools</div>
        <h2 className="mt-3 font-display text-2xl font-semibold md:text-3xl">Put official demand in context</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {RELATED.map((n) => (
            <Link key={n.to} to={n.to} className="group rounded-xl border border-border/60 bg-card/60 p-5 transition-colors hover:border-gold/50">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{n.label}</div>
              <div className="mt-2 font-medium text-foreground group-hover:text-gold">{n.title}</div>
              <p className="mt-2 text-sm text-muted-foreground">{n.body}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="container mx-auto mt-16 max-w-7xl px-4">
        <div className="rounded-2xl border border-gold/30 bg-card/70 p-6 md:p-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Macro frames the bias</div>
          <h2 className="mt-3 max-w-2xl font-display text-2xl font-semibold md:text-3xl">Long term demand sets the backdrop. Structure sets the trade.</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            XAU MacroLens™ combines macro readings with higher timeframe structure in one score, and every published decision on the desk
            is recorded in the verified track record.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild variant="gold">
              <Link to="/macrolens">
                Explore MacroLens <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/track-record">See the track record</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="container mx-auto mt-16 max-w-4xl px-4 pb-6">
        <h2 className="font-display text-2xl font-semibold md:text-3xl">Frequently asked questions</h2>
        <div className="mt-6 divide-y divide-border/60 rounded-xl border border-border/60 bg-card/60">
          {FAQ.map((f) => (
            <details key={f.q} className="group p-5">
              <summary className="cursor-pointer list-none font-medium text-foreground">{f.q}</summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <MacroToolsStrip />
      <div className="pb-16" />
    </Layout>
  );
}
